import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

export default function About() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    const section = sectionRef.current;
    if (section) {
      const elements = section.querySelectorAll(".reveal-left, .reveal-right");
      elements.forEach((el) => observer.observe(el));
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" ref={sectionRef} className="py-20 md:py-28 lg:py-32 bg-background-50">
      <div className="px-4 md:px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Content */}
            <div className="reveal-left">
              <span className="text-accent-500 text-xs font-semibold tracking-[0.25em] uppercase mb-4 block">
                About Askari
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-primary-700 leading-tight mb-6">
                Trusted Security
                <br />
                Professionals
              </h2>
              <div className="space-y-4 text-foreground-700 text-base leading-relaxed mb-8">
                <p>
                  Askari Security Services is a licensed and insured private security
                  firm headquartered in Los Angeles, California. We are court-qualified
                  experts in Security Guard Standard of Care, specializing in unarmed
                  operations, executive protection, and high-risk event environments.
                </p>
                <p>
                  Trusted by communities and businesses for over a decade, we provide
                  comprehensive protection services across a wide range of industries.
                  Our mission is to safeguard people, property, and peace of mind.
                </p>
                <p>
                  We focus on reliability, discretion, and the highest professional
                  standards. Every team member is thoroughly vetted and trained to
                  ensure 24/7 operational readiness.
                </p>
              </div>

              <div className="flex flex-wrap gap-3">
                <Link
                  to="/services"
                  className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold px-6 py-3 rounded-md transition-all duration-300 whitespace-nowrap text-sm"
                >
                  Our Services
                  <i className="ri-arrow-right-line" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 border border-primary-500 hover:bg-primary-500/10 text-primary-700 font-semibold px-6 py-3 rounded-md transition-all duration-300 whitespace-nowrap text-sm"
                >
                  Contact Us
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>

            {/* Right Stats & Image */}
            <div className="reveal-right">
              <div className="relative">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src="/images/about-askari-security-services.jpg"
                    alt="Askari Security Services professional team"
                    className="w-full h-[400px] md:h-[500px] object-cover object-top"
                    loading="lazy"
                  />
                </div>

                {/* Stats Overlay */}
                <div className="absolute -bottom-8 -left-4 md:-left-8 right-4 md:right-8">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-primary-500 text-background-50 p-4 md:p-6 rounded-lg">
                      <div className="font-heading text-2xl md:text-3xl font-bold">
                        12+
                      </div>
                      <div className="text-xs md:text-sm text-background-50/80">
                        Years Experience
                      </div>
                    </div>
                    <div className="bg-accent-500 text-background-50 p-4 md:p-6 rounded-lg">
                      <div className="font-heading text-2xl md:text-3xl font-bold">
                        100%
                      </div>
                      <div className="text-xs md:text-sm text-background-50/80">
                        Licensed Guards
                      </div>
                    </div>
                    <div className="bg-primary-700 text-background-50 p-4 md:p-6 rounded-lg">
                      <div className="font-heading text-2xl md:text-3xl font-bold">
                        750+
                      </div>
                      <div className="text-xs md:text-sm text-background-50/80">
                        Clients Protected
                      </div>
                    </div>
                    <div className="bg-secondary-500 text-background-50 p-4 md:p-6 rounded-lg">
                      <div className="font-heading text-2xl md:text-3xl font-bold">
                        24/7
                      </div>
                      <div className="text-xs md:text-sm text-background-50/80">
                        Always On Duty
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Credentials Bar */}
          <div className="mt-20 md:mt-24 pt-10 border-t border-background-200">
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
              <div className="flex items-center gap-3 text-foreground-600">
                <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-full">
                  <i className="ri-shield-check-line text-primary-600 text-lg" />
                </div>
                <div>
                  <div className="font-semibold text-sm text-foreground-700">
                    Licensed &amp; Bonded
                  </div>
                  <div className="text-xs text-foreground-500">CA PPO #17831</div>
                </div>
              </div>
              <div className="flex items-center gap-3 text-foreground-600">
                <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-full">
                  <i className="ri-headphone-line text-accent-600 text-lg" />
                </div>
                <div>
                  <div className="font-semibold text-sm text-foreground-700">
                    24/7 Dispatch Center
                  </div>
                  <div className="text-xs text-foreground-500">
                    Round-the-clock monitoring
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 text-foreground-600">
                <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-full">
                  <i className="ri-medal-line text-secondary-600 text-lg" />
                </div>
                <div>
                  <div className="font-semibold text-sm text-foreground-700">
                    Veteran-Owned
                  </div>
                  <div className="text-xs text-foreground-500">
                    Military-trained leadership
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 text-foreground-600">
                <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-full">
                  <i className="ri-verified-badge-line text-primary-600 text-lg" />
                </div>
                <div>
                  <div className="font-semibold text-sm text-foreground-700">
                    Fully Insured
                  </div>
                  <div className="text-xs text-foreground-500">
                    $5M liability coverage
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}